// Editor decorations that help without changing the document text:
//   📅 beside every date in the text (change it) and after an empty date label
//      such as "Follow-up:" (insert one)
//   ⓘ  at the end of a line pre-filled from captured visit data, naming its
//      source ("Vitals stage · Nurse Priya · 09:20")
// Decorations are view-only: they never reach the saved markdown.
// See docs/HMS_V4_AI_CONTEXT_BUILDING_ASSESSMENT.md §5.2 C2 and C11.
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { DATE_IN_TEXT_RE, EMPTY_DATE_LABEL_RE } from './smartFields';

export interface DateRequest {
  /** Range to replace; from === to means "insert here". */
  from: number;
  to: number;
  /** The date text being changed, or null when inserting. */
  current: string | null;
  anchor: HTMLElement;
}

export interface SourceMarker {
  /** The line's leading label, e.g. "Vitals" matches "Vitals: BP …". */
  label: string;
  title: string;
}

export interface HeadingSection {
  key: string;
  heading: string;
}

export interface DocAssistState {
  onDateRequest?: (request: DateRequest) => void;
  sources: SourceMarker[];
  editable: boolean;
  /** C6 — line index (one per textblock, in document order) to its detected heading, from documentSections.ts's detectSections(). */
  headingLines?: Map<number, HeadingSection>;
  onRegenerateSection?: (section: HeadingSection) => void;
}

export interface DocAssistOptions {
  /** Read on every rebuild, so callers can change it without recreating the editor. */
  getState: () => DocAssistState;
}

export const docAssistKey = new PluginKey<DecorationSet>('docAssist');

function dateButton(onRequest: (r: DateRequest) => void, from: number, to: number, current: string | null): HTMLElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.contentEditable = 'false';
  el.textContent = '📅';
  el.title = current ? `Change date (${current})` : 'Insert a date';
  el.setAttribute('aria-label', el.title);
  el.className = 'mx-0.5 inline-flex h-5 w-5 items-center justify-center rounded align-middle text-[11px] hover:bg-teal-light';
  // mousedown, not click: keeps the editor's selection instead of blurring it first.
  el.addEventListener('mousedown', (event) => {
    event.preventDefault();
    onRequest({ from, to, current, anchor: el });
  });
  return el;
}

function sourceMarker(title: string): HTMLElement {
  const el = document.createElement('span');
  el.contentEditable = 'false';
  el.textContent = 'ⓘ';
  el.title = title;
  el.setAttribute('aria-label', title);
  el.className = 'ml-1 cursor-help select-none text-[11px] text-ink-muted';
  return el;
}

function regenerateButton(onRegenerate: (section: HeadingSection) => void, section: HeadingSection): HTMLElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.contentEditable = 'false';
  el.textContent = '↻';
  el.title = `Regenerate this section (${section.heading})`;
  el.setAttribute('aria-label', el.title);
  el.className = 'ml-1 inline-flex h-5 w-5 items-center justify-center rounded align-middle text-[11px] hover:bg-teal-light';
  // mousedown, not click: keeps the editor's selection instead of blurring it first.
  el.addEventListener('mousedown', (event) => {
    event.preventDefault();
    onRegenerate(section);
  });
  return el;
}

function buildDecorations(doc: PMNode, state: DocAssistState): DecorationSet {
  const decorations: Decoration[] = [];
  // documentModel.ts's contract is one markdown line = one textblock, always,
  // in document order — so this counter's value at each textblock visit is
  // exactly that line's index into detectSections()'s result.
  let lineIndex = -1;

  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    lineIndex += 1;

    // The paragraph's text with each inline atom (a fill-in chip) as one
    // placeholder character, and the document position of each character, so
    // regex offsets map straight back to positions.
    let text = '';
    const positions: number[] = [];
    node.forEach((child, offset) => {
      if (child.isText && child.text) {
        for (let i = 0; i < child.text.length; i++) positions.push(pos + 1 + offset + i);
        text += child.text;
      } else {
        positions.push(pos + 1 + offset);
        text += '￼';
      }
    });
    const end = pos + node.nodeSize - 1;
    const at = (index: number) => (index < positions.length ? positions[index]! : end);

    if (state.editable && state.onDateRequest) {
      for (const match of text.matchAll(DATE_IN_TEXT_RE)) {
        const from = at(match.index ?? 0);
        const to = at((match.index ?? 0) + match[0].length);
        decorations.push(Decoration.widget(to, () => dateButton(state.onDateRequest!, from, to, match[0]), { side: 1, key: `date-${from}-${to}` }));
      }
      if (EMPTY_DATE_LABEL_RE.test(text)) {
        decorations.push(Decoration.widget(end, () => dateButton(state.onDateRequest!, end, end, null), { side: 1, key: `date-label-${end}` }));
      }
    }

    const source = state.sources.find((s) => text.startsWith(`${s.label}:`));
    if (source) {
      decorations.push(Decoration.widget(end, () => sourceMarker(source.title), { side: 1, key: `source-${end}-${source.label}` }));
    }

    const section = state.headingLines?.get(lineIndex);
    if (state.editable && state.onRegenerateSection && section) {
      decorations.push(Decoration.widget(end, () => regenerateButton(state.onRegenerateSection!, section), { side: 1, key: `regen-${end}` }));
    }
    return false;
  });

  return DecorationSet.create(doc, decorations);
}

export const DocAssistExtension = Extension.create<DocAssistOptions>({
  name: 'docAssist',

  addOptions() {
    return { getState: () => ({ sources: [], editable: true }) };
  },

  addProseMirrorPlugins() {
    const { getState } = this.options;
    return [
      new Plugin<DecorationSet>({
        key: docAssistKey,
        state: {
          init: (_config, editorState) => buildDecorations(editorState.doc, getState()),
          // Rebuilt when the text changes, or when the editor asks (sources or
          // editability changed) by setting this plugin's meta on a transaction.
          apply: (tr, previous, _oldState, newState) =>
            tr.docChanged || tr.getMeta(docAssistKey) ? buildDecorations(newState.doc, getState()) : previous.map(tr.mapping, tr.doc),
        },
        props: {
          decorations(editorState) {
            return docAssistKey.getState(editorState);
          },
        },
      }),
    ];
  },
});
